import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Headphones, ArrowLeft, Search, Music, Mic, Radio, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API } from "@/App";
import axios from "axios";

const ListeningPage = () => {
  const navigate = useNavigate();
  const [listenings, setListenings] = useState([]);
  const [dailyListening, setDailyListening] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    fetchListenings();
  }, []);

  const fetchListenings = async () => {
    try {
      const [listeningsRes, dailyRes] = await Promise.all([
        axios.get(`${API}/content/listening`),
        axios.get(`${API}/content/daily`)
      ]);
      setListenings(listeningsRes.data.listenings);
      setDailyListening(dailyRes.data.listening);
    } catch (error) {
      console.error("Error fetching listenings:", error);
    } finally {
      setLoading(false);
    }
  };

  const types = [...new Set(listenings.map(l => l.type))].sort();
  const categories = [...new Set(listenings.map(l => l.category))].sort();

  const filteredListenings = listenings.filter(listening => {
    const matchesSearch = listening.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === "all" || listening.type === selectedType;
    const matchesCategory = selectedCategory === "all" || listening.category === selectedCategory;
    return matchesSearch && matchesType && matchesCategory;
  });

  const getTypeIcon = (type) => {
    switch(type?.toLowerCase()) {
      case 'podcast': return <Mic className="w-6 h-6" />;
      case 'music': return <Music className="w-6 h-6" />;
      case 'app': return <Radio className="w-6 h-6" />;
      default: return <Headphones className="w-6 h-6" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen hero-gradient flex items-center justify-center">
        <div className="animate-breathe">
          <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center">
            <Headphones className="w-8 h-8 text-accent" strokeWidth={1.5} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient" data-testid="listening-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground hover:text-foreground"
            data-testid="listening-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Headphones className="w-5 h-5 text-accent" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Listening</span>
          </div>
          <div className="w-24"></div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-3">
            Recommended Listening
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            {listenings.length} podcasts, music, and audio experiences for your journey
          </p>
        </div>

        {/* Today's Recommendation */}
        {dailyListening && (
          <Card className="glass-card rounded-3xl border-0 mb-10 overflow-hidden" data-testid="daily-listening-feature">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-2xl bg-accent/20 flex items-center justify-center flex-shrink-0">
                  {getTypeIcon(dailyListening.type)}
                </div>
                <div>
                  <span className="text-xs text-accent uppercase tracking-wide">Today's Recommendation</span>
                  <h2 className="font-heading text-2xl font-semibold text-foreground mt-2 mb-1">
                    {dailyListening.title}
                  </h2>
                  <p className="text-sm text-muted-foreground mb-3">{dailyListening.type}</p>
                  <p className="text-foreground leading-relaxed mb-4">
                    {dailyListening.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="inline-block text-xs bg-accent/10 text-accent px-3 py-1 rounded-full">
                      {dailyListening.category}
                    </span>
                    {dailyListening.url && (
                      <a
                        href={dailyListening.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-accent hover:text-accent/80 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 rounded-md px-2 py-1"
                        aria-label={`Listen to ${dailyListening.title} (opens in new tab)`}
                        data-testid="daily-listening-link"
                      >
                        <ExternalLink className="w-4 h-4" aria-hidden="true" />
                        <span>Listen now</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-xl bg-white/50"
              data-testid="listening-search"
            />
          </div>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-4 py-2 rounded-xl bg-white/50 border border-border text-foreground"
            data-testid="listening-type-filter"
          >
            <option value="all">All Types</option>
            {types.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 rounded-xl bg-white/50 border border-border text-foreground"
            data-testid="listening-category-filter"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Listenings List */}
        <div className="grid md:grid-cols-2 gap-4" role="list" aria-label="Listening recommendations">
          {filteredListenings.map((listening) => (
            <Card
              key={listening.id}
              className="glass-card rounded-2xl border-0 card-hover"
              data-testid={`listening-${listening.id}`}
              role="listitem"
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0 text-accent">
                    {getTypeIcon(listening.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-base font-semibold text-foreground truncate">
                      {listening.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mb-2">
                      {listening.type}
                    </p>
                    <p className="text-sm text-foreground/80 leading-relaxed line-clamp-2 mb-2">
                      {listening.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-block text-xs bg-muted px-2 py-0.5 rounded-full">
                        {listening.category}
                      </span>
                      {listening.url && (
                        <a
                          href={listening.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs text-accent hover:text-accent/80 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 rounded-md px-1.5 py-0.5"
                          aria-label={`Listen to ${listening.title} (opens in new tab)`}
                          data-testid={`listening-link-${listening.id}`}
                        >
                          <ExternalLink className="w-3 h-3" aria-hidden="true" />
                          <span>Listen</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredListenings.length === 0 && (
          <div className="text-center py-12">
            <Headphones className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">No listening content found matching your search.</p>
          </div>
        )}

        {/* Closing Message */}
        <div className="text-center mt-12 py-8">
          <p className="font-handwritten text-2xl text-accent">
            "Let sound be the bridge to your inner sanctuary"
          </p>
        </div>
      </main>
    </div>
  );
};

export default ListeningPage;
